// What the WHOLE app should badge (the browser tab title, the favicon) — the web twin of the Mac's
// Dock badge (ios/SourcesMac/MacAttentionService.swift `refreshBadge`), plus the Mac sidebar's
// "folded" collapsed categories (MacChannelSidebar.swift `sectionsList`).
//
// The count is derived from the SAME `unreadState` the sidebar rows use — read out of the same
// snapshot, never counted a second way: a badge that disagrees with the sidebar behind it teaches
// the reader that neither number is worth believing. Mentions if there are any, otherwise a plain
// dot for "something unread" (Mattermost's convention, and the phone's): a count that mixes
// mentions with ordinary unread channels is a number nobody can act on.
//
// ONE DELIBERATE DIFFERENCE FROM THE MAC: a channel of a team the reader SILENCED (`TeamMute`)
// does not badge. The Mac's Dock badge still counts it (only its banners and its team tile go
// quiet); TeamMute's own contract is "muting is about what INTERRUPTS", and a tab title is the
// web's interruption. DMs/GMs carry no team and are never swallowed by a team mute.
import { isRootPost, unreadState, visibleRows } from './channelListDerivation';
import type { ChannelListSection } from './channelListDisplayModels';
import type { ChannelMemberRecord, ChannelRecord, PostRecord } from './records';
import { TeamMute } from './teamMute';

export interface Attention {
  /** Unmuted mentions across every team and conversation. */
  mentionCount: number;
  /** Anything unread and unmuted anywhere (bold rows), mentions or not. */
  hasUnread: boolean;
}

export function compute(args: {
  channels: readonly ChannelRecord[];
  membersByChannelId: ReadonlyMap<string, ChannelMemberRecord>;
  /** Newest post per channel — the "your own newest root post is never unread" shortcut. */
  latestPostByChannelId?: ReadonlyMap<string, PostRecord> | Readonly<Record<string, PostRecord>>;
  currentUserId: string;
  /** Channels inside a muted sidebar category (the second, independent mute). */
  mutedChannelIds?: ReadonlySet<string>;
  /** Teams the reader silenced (`satori_team_mute`). */
  mutedTeamIds?: ReadonlySet<string>;
}): Attention {
  const latest = args.latestPostByChannelId;
  const latestOf = (id: string): PostRecord | undefined =>
    latest == null ? undefined : latest instanceof Map ? latest.get(id) : (latest as Record<string, PostRecord>)[id];
  const mutedChannels = args.mutedChannelIds ?? new Set<string>();
  const mutedTeams = args.mutedTeamIds ?? new Set<string>();
  let mentionCount = 0;
  let hasUnread = false;
  for (const channel of args.channels) {
    if (channel.deleteAt !== 0) continue;
    if (TeamMute.isMuted(channel.teamId, mutedTeams)) continue;
    const post = latestOf(channel.id);
    const state = unreadState(channel, args.membersByChannelId.get(channel.id), {
      latestPostIsOwnRoot: post ? post.userId === args.currentUserId && isRootPost(post) : false,
      categoryMuted: mutedChannels.has(channel.id),
    });
    if (state.isMuted) continue;
    mentionCount += state.mentionCount;
    if (state.isUnread) hasUnread = true;
  }
  return { mentionCount, hasUnread };
}

/** The Dock's label: "N" for mentions, "•" for unread without mentions, null for nothing. */
export function badgeLabel(attention: Attention): string | null {
  if (attention.mentionCount > 0) return String(attention.mentionCount);
  return attention.hasUnread ? '•' : null;
}

/**
 * Collapsed categories with NOTHING to surface fold into one row of pills instead of a header
 * each (Mac 2026-10-09, "you are cluttering the ui"). Never while searching: a search shows every
 * match. Verbatim with the Mac, which asks `visibleRows` WITHOUT the open conversation.
 */
export function foldedSectionIds(sections: readonly ChannelListSection[], isSearching: boolean): Set<string> {
  const out = new Set<string>();
  if (isSearching) return out;
  for (const s of sections) {
    if (s.collapsed && visibleRows(s.rows, { collapsed: true, isSearching: false }).length === 0) out.add(s.id);
  }
  return out;
}

export const SidebarAttention = { compute, badgeLabel, foldedSectionIds } as const;
